import { NgModule, isDevMode, inject, provideAppInitializer  } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common'; 
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { RouterModule } from '@angular/router';    
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getAnalytics, provideAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { environment } from '../environments/environment';
import { IS_NATIVE } from './shared/platform.util';

// MODULOS
import { AppRoutingModule } from './app-routing.module';
import { LoginModule } from './login/login.module';
import { InicioModule } from './inicio/inicio.module';
import { MyAccountModule } from './my-account/my-account.module';
import { NotificationsModule } from './notifications/notifications.module';
import { MoreModule } from './more/more.module';
import { OrderComercioModule } from './order-comercio/order-comercio.module';
import { OrderPrestadorModule } from './order-prestador/order-prestador.module';
import { OrderEmergenciasModule } from './order-emergencias/order-emergencias.module';

// COMPONENTES
import { AppComponent } from './app.component';
import { NavbarComponent } from './navbar/navbar.component';
import { LiaChatWidgetComponent } from './lia-chat-widget/lia-chat-widget.component';

// SERVICIOS
import { NetworkService } from './services/network.service';
import { AuthService } from './services/auth.service';
import { UsuarioService } from './services/usuario.service';
import { NotificacionService } from './services/notificacion.service';
import { OrderPrestadorService } from './services/order-prestador.service';
import { OrderComercioService } from './services/order-comercio.service';
import { UpdateCheckService } from './services/update-check.service';
import { AnalyticsService } from './services/analytics.service';
import { AuthInterceptor } from './shared/auth.interceptor';

const ENABLE_SW  = !isDevMode() && !IS_NATIVE;
const HAS_MEASUREMENT_ID = !!(environment.firebase as { measurementId?: string }).measurementId;
const FIREBASE_PROVIDERS = [
  provideFirebaseApp(() => initializeApp(environment.firebase)),
  ...(!IS_NATIVE
    ? [
        provideAuth(() => {
          const auth = getAuth();
          auth.useDeviceLanguage?.();
          return auth;
        }),
        // Analytics web (flujo "pwa"): solo si ya se configuró el measurementId.
        // En nativo NO se agrega: eso lo maneja @capacitor-firebase/analytics.
        ...(HAS_MEASUREMENT_ID
          ? [
              provideAnalytics(() => getAnalytics()),
              ScreenTrackingService, // page_view automático por navegación
              UserTrackingService,   // asocia el uid de Firebase Auth
            ]
          : []),
      ]
    : []),
];

@NgModule({ declarations: [
        AppComponent,
        NavbarComponent,
        LiaChatWidgetComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        CommonModule,
        RouterModule,
        AppRoutingModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: ENABLE_SW,
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000'
        }),
        LoginModule,
        InicioModule,
        MyAccountModule,
        NotificationsModule,
        MoreModule,
        OrderComercioModule,
        OrderPrestadorModule,
        OrderEmergenciasModule,
        NgbModule,
        NgxMaskDirective, NgxMaskPipe], 
    providers: [
        ...FIREBASE_PROVIDERS,
        AuthService,
        UsuarioService,
        NotificacionService,
        OrderPrestadorService,
        OrderComercioService,
        UpdateCheckService,
        NetworkService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        provideNgxMask(),
        // 0) Analytics (no bloquea): nativo -> plugin; web -> SDK JS
        provideAppInitializer(() => {
            const _analytics = inject(AnalyticsService);
            return _analytics.init();
        }),
        // 1) Inicializar NetworkService primero (no bloquea)
        provideAppInitializer(() => {
            const _network = inject(NetworkService);
            return _network.init(); // <-- devuelve Promise OK
        }),
        // 2) Luego tu ensureSession con timeout
        provideAppInitializer(() => {
            const _authService = inject(AuthService);
            // 1) Inicializar NetworkService primero (no bloquea)
            return (async () => {        
                const timeoutMs = 8000;
                try {
                await Promise.race([
                    _authService.ensureSession(),
                    new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('ensureSession timeout')), timeoutMs)
                    ),
                ]);
                } catch (e) {
                console.warn('AppInitializer: ensureSession falló o timeout', e);
                // IMPORTANTE: no lanzar error, para que la app arranque igual
                }

                // Cualquier “precarga” que necesites, hacelas después (ya authReady=true)
                // await usuario.getUbicacionDefault(usuario.usuario().id!);
            })();
        }),
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }